import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { glossary } from '../data/glossary'

interface InfoTipProps {
  /** Glossary key — must match a key in data/glossary.ts */
  term: string
  /** Override placement: 'top' | 'bottom' | 'left' | 'right' */
  placement?: 'top' | 'bottom' | 'left' | 'right'
}

/**
 * A tiny circled ℹ button.
 * Click/hover → shows a tooltip with the term title + explanation.
 * Uses a portal to render outside of overflow:hidden parents.
 */
export function InfoTip({ term, placement = 'top' }: InfoTipProps) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const tipRef = useRef<HTMLDivElement>(null)
  const entry = glossary[term]

  const calcPosition = useCallback(() => {
    if (!btnRef.current) return
    const rect = btnRef.current.getBoundingClientRect()
    const tipW = 280
    let top = 0
    let left = 0

    if (placement === 'bottom') {
      top = rect.bottom + 8
      left = rect.left + rect.width / 2 - tipW / 2
    } else if (placement === 'left') {
      top = rect.top + rect.height / 2
      left = rect.left - tipW - 8
    } else if (placement === 'right') {
      top = rect.top + rect.height / 2
      left = rect.right + 8
    } else {
      // top (default)
      top = rect.top - 8
      left = rect.left + rect.width / 2 - tipW / 2
    }

    // Clamp to viewport
    if (left < 8) left = 8
    if (left + tipW > window.innerWidth - 8) left = window.innerWidth - tipW - 8

    setPos({ top, left })
  }, [placement])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (btnRef.current && btnRef.current.contains(e.target as Node)) return
      if (tipRef.current && tipRef.current.contains(e.target as Node)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Recalculate position on scroll/resize
  useEffect(() => {
    if (!open) return
    calcPosition()
    window.addEventListener('scroll', calcPosition, true)
    window.addEventListener('resize', calcPosition)
    return () => {
      window.removeEventListener('scroll', calcPosition, true)
      window.removeEventListener('resize', calcPosition)
    }
  }, [open, calcPosition])

  if (!entry) return null

  const handleOpen = () => {
    calcPosition()
    setOpen(true)
  }

  const tooltip = open && pos ? createPortal(
    <div
      className="infotip-popup-portal"
      ref={tipRef}
      style={{
        position: 'fixed',
        top: placement === 'top' ? undefined : pos.top,
        bottom: placement === 'top' ? `${window.innerHeight - pos.top}px` : undefined,
        left: pos.left,
        zIndex: 99999,
        width: 280,
      }}
    >
      <div className="infotip-title">{entry.title}</div>
      <div className="infotip-body">{entry.body}</div>
    </div>,
    document.body
  ) : null

  return (
    <span className="infotip-wrap">
      <button
        ref={btnRef}
        className="infotip-btn"
        aria-label={`Info: ${entry.title}`}
        onClick={(e) => { e.stopPropagation(); open ? setOpen(false) : handleOpen() }}
        onMouseEnter={handleOpen}
        onMouseLeave={() => setOpen(false)}
      >
        ℹ
      </button>
      {tooltip}
    </span>
  )
}
